import axios from 'axios'

import store from '@/store'
import router from '@/router'
import { getToken } from '@/utils/auth'
import { getApiBase } from '@/config/apiServer'
import { jsonlint } from "@/utils/jsonlint"
import { Message, MessageBox } from 'element-ui'
import { clearLoginInfo } from '@/utils'
import moment from 'moment-timezone'

/** 僅此類路徑不帶 token、且 POST 不併入 timeZone（其餘如 /auth/getUserInfo 須帶 token） */
function isAuthLoginOnly(url) {
  const u = String(url || '')
  if (u.indexOf('/auth/doLogin') !== -1) return true
  if (u.indexOf('/overseas/auth/doLogin') !== -1) return true
  if (u.indexOf('/coffee/leo/membership/doLogin') !== -1) return true
  if (u.indexOf('/coffee/leo/membership/sendRegisterCode') !== -1) return true
  if (u.indexOf('/coffee/leo/membership/register') !== -1) return true
  return false
}

/** 是否為「登入請求」：優先讀 api/login 設定的 xrrossLoginAttempt，避免 URL 被改寫導致誤判 */
function isLoginDoLoginRequest(config) {
  if (!config) return false
  if (config.xrrossLoginAttempt === true) return true
  const u = String(config.url || '')
  const base = String(config.baseURL || getApiBase() || '')
  if (u.indexOf('/auth/doLogin') !== -1) return true
  if (u.indexOf('/overseas/auth/doLogin') !== -1) return true
  if (u.indexOf('/coffee/leo/membership/doLogin') !== -1) return true
  if (u.indexOf('/coffee/leo/membership/sendRegisterCode') !== -1) return true
  if (u.indexOf('/coffee/leo/membership/register') !== -1) return true
  if (base && u) {
    const joined = (base.replace(/\/$/, '') + '/' + u.replace(/^\//, ''))
    if (joined.indexOf('/auth/doLogin') !== -1) return true
    if (joined.indexOf('/overseas/auth/doLogin') !== -1) return true
    if (joined.indexOf('/coffee/leo/membership/doLogin') !== -1) return true
    if (joined.indexOf('/coffee/leo/membership/sendRegisterCode') !== -1) return true
    if (joined.indexOf('/coffee/leo/membership/register') !== -1) return true
  }
  return false
}

// API 基底由 getApiBase()（建置／同源／dev:staging 代理）決定；下載/上傳等請用 getApiBase()
export { getApiBase }
export const apiBase = process.env.VUE_APP_BASE_API + (process.env.VUE_APP_API_PREFIX || '')
/** 毫秒；逾時後 axios 會 ECONNABORTED（約 120 秒，非 1200 秒） */
const DEFAULT_TIMEOUT_MS = 1000 * 120
const service = axios.create({
  baseURL: apiBase,
  timeout: DEFAULT_TIMEOUT_MS,
  transformResponse: [function (data) {
    if (typeof data !== 'string' || !data) return data
    const t = data.trim()
    if (t.startsWith('<!') || t.startsWith('<html')) {
      console.error('API 回傳 HTML，非 JSON')
      return data
    }
    // 504/代理錯誤常為純文字 "Error occured while trying to proxy..."，勿送 jsonlint
    if (!/^[\[{]/.test(t)) {
      return data
    }
    try {
      return jsonlint.parse(data)
    } catch (e) {
      console.error('API response parse error:', e)
      return data
    }
  }]
})

// request interceptor
service.interceptors.request.use(
  config => {
    const url = String(config.url || '')
    const dynamicBase = getApiBase()
    // X600H / XT90C2 本地聯調在 Gateway(20000) 走 /coffee/...；若誤帶 /api 前綴會 404。
    // 僅對 simulator 背景路徑做前綴修正。Leo /coffee/leo/knowledge/* 須與其他後台一致走 /api/tokyo/coffee/...，
    // 若一併 strip 會變成 http://host:port/coffee/...，Gateway 未轉發時會 404。
    if (url.indexOf('/coffee/background/x600h') === 0 || url.indexOf('/coffee/background/xt90c2') === 0) {
      config.baseURL = String(dynamicBase).replace(/\/api(?:\/tokyo)?$/, '')
    } else {
      config.baseURL = dynamicBase
    }
    if (!isAuthLoginOnly(url) && getToken()) {
      // 让每个请求携带token-- ['X-Token']为自定义key 请根据实际情况自行修改
      config.headers['token'] = getToken()
    }
    config.headers['loginType'] = "MachineSys"
    if (config.method === 'post') {
      if(!config.url.includes("/delete")){
        // 僅 doLogin 僅送 account/password；加 timeZone 少數 Gateway 會 400
        const skipTimeZone =
          config.url && isAuthLoginOnly(config.url)
        const isFormData =
          typeof FormData !== 'undefined' && config.data instanceof FormData
        if(config.data!=undefined){
          config.data = skipTimeZone || isFormData
            ? config.data
            : {
                timeZone: moment.tz.guess(true),
                ...config.data
              }
        }else if(config.params!=undefined){
          config.params = {
            timeZone: moment.tz.guess(true),
           ...config.params
          }
        }
      
      }
     
      } else if (config.method === 'get') {
      config.params = {
        timeZone: moment.tz.guess(true),
       ...config.params
      }
      }
    // 部分後端／代理對 application/json;charset=UTF-8 比對過嚴會 415；改為不帶 charset 的 application/json
    const m = String(config.method || '').toLowerCase()
    if ((m === 'post' || m === 'put' || m === 'patch') && !(config.data instanceof FormData)) {
      const h = config.headers || (config.headers = {})
      const ct = h['Content-Type'] || h['content-type']
      if (typeof ct === 'string') {
        const low = ct.toLowerCase()
        if (low.indexOf('application/json') === 0 && low.indexOf('charset=') !== -1) {
          h['Content-Type'] = 'application/json'
          if (h['content-type'] !== undefined) delete h['content-type']
        }
      }
    }
    return config
  },
  error => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[request]', error && error.message ? error.message : error)
    }
    return Promise.reject(error)
  }
)

// response interceptor
service.interceptors.response.use(
  /**
   * 下面的注释为通过在response里，自定义code来标示请求状态
   * 当code返回如下情况则说明权限有问题，登出并返回到登录页
   * 如想通过 xmlhttprequest 来状态码标识 逻辑可写在下面error中
   * 以下代码均为样例，请结合自生需求加以修改，若不需要，则可删除
   */
  response => {
    const res = response.data
    const cfg = response.config || {}
    const isLoginDoLogin = isLoginDoLoginRequest(cfg)
    if (typeof res !== 'object' || res === null) {
      Message({
        message: '伺服器回應格式錯誤',
        type: 'error',
        duration: 5 * 1000
      })
      return Promise.reject(new Error('Invalid response'))
    }
    if (res.code !== 200) {
      if (res.code === 401) {
        if (isLoginDoLogin) {
          // doLogin：業務碼 401 為帳密錯誤等
          Message({
            message: res.message == null ? (res.msg || '登入失敗') : res.message,
            type: 'error',
            duration: 5 * 1000
          })
        } else {
          /** 路由守衛內並行請求選單／權限時，勿在此處 FedLogOut，否則會搶在 merge 靜態路由前整頁踢回登入 */
          if (cfg.xrrossSkipGlobal401Logout) {
            return Promise.reject(res)
          }
          if (getToken()) {
            Message({
              message:
                res.message == null
                  ? (res.msg || '登入已過期，請重新登入')
                  : res.message,
              type: 'error',
              duration: 5 * 1000
            })
          }
          store.dispatch('FedLogOut').then(() => {
            clearLoginInfo()
            router.push({ name: 'login' }) // 为了重新实例化vue-router对象 避免bug
          })
        }
      } else {
        if (!cfg.xrrossSkipBusinessErrorToast) {
          Message({
            message: res.message == null ? (res.msg || '請求失敗') : res.message,
            type: 'error',
            duration: 5 * 1000
          })
        }
      }
      return Promise.reject(res)
    } else {
      return response.data
    }
  },
  error => {
    if (error.config && error.config.silentGlobalError) {
      return Promise.reject(error)
    }
    if (error.response && error.response.status === 401 && isLoginDoLoginRequest(error.config)) {
      const d = error.response.data
      let m = '登入失敗'
      if (d && typeof d === 'object') {
        m = d.message || d.msg || m
      } else if (typeof d === 'string' && d.trim()) {
        m = d.trim().slice(0, 300)
      }
      Message({
        message: m,
        type: 'error',
        duration: 5 * 1000
      })
      return Promise.reject(error)
    }
    const timeoutSec =
      error.config && error.config.timeout
        ? Math.round(Number(error.config.timeout) / 1000)
        : Math.round(DEFAULT_TIMEOUT_MS / 1000)
    const baseUrl = (error.config && error.config.baseURL) || ''
    const stagingHint =
      error.code === 'ECONNABORTED' &&
      (String(baseUrl).indexOf('18.177.93.7') !== -1 ||
        String(baseUrl).indexOf('__staging111') !== -1 ||
        String(baseUrl).indexOf('staging.xrross-soc.com') !== -1)
        ? ' 連 111／Staging 請確認 API 與頁面同源（https://staging.xrross-soc.com/api/tokyo 或 http://IP:8089/api/tokyo），勿在 HTTPS 頁請求 http://。本機 dev 連 111 請用 npm run dev:staging。若仍失敗請查 gateway-tokyo / Nacos / Background。'
        : ''
    const is504 = error.response && error.response.status === 504
    const baseFor504 = String(
      (error.config && error.config.baseURL) || getApiBase() || ''
    ).trim()
    const is111Staging504 =
      baseFor504.includes('__staging111') ||
      baseFor504.includes('18.177.93.7') ||
      baseFor504.includes('staging.xrross-soc.com')
    const msg504 = is111Staging504
      ? '連線逾時 (504)：Staging/111 鏈路（Nginx→Gateway→Background）未及時回應。請在 111 查 Background、Nacos、Gateway 路由/StripPrefix，並視情況調高 nginx proxy_read_timeout。'
      : '連線逾時 (504)：上游（Nginx→Gateway→Background）未及時回應。請在目前頁面所連後端檢查上述服務與 Nacos 註冊，並視情況調高 Nginx proxy_read_timeout（此訊息不再預設為 111）。'
    const payload400 = error.response && error.response.status === 400 && error.response.data
    let msg400 = ''
    if (payload400) {
      if (typeof payload400 === 'string') {
        msg400 = payload400.trim().slice(0, 300)
      } else if (typeof payload400 === 'object') {
        msg400 =
          payload400.message ||
          payload400.msg ||
          payload400.error ||
          (payload400.errors && JSON.stringify(payload400.errors)) ||
          ''
      }
    }
    const msg =
      error.msg ||
      (is504 && msg504) ||
      (error.response &&
        error.response.status === 400 &&
        (msg400
          ? `請求被拒絕 (400)：${msg400}`
          : '請求被拒絕 (400)：請開 F12 → Network → 該請求 → Response 查看後端說明')) ||
      error.message ||
      (error.response && error.response.data && typeof error.response.data === 'object' && (error.response.data.message || error.response.data.msg)) ||
      (error.response && error.response.status === 404 && '介面不存在 (404)') ||
      (error.response && error.response.status >= 500 && !is504 && '伺服器錯誤，請稍後再試') ||
      (error.code === 'ECONNABORTED' &&
        `請求逾時（前端等待上限約 ${timeoutSec} 秒，非 1200 秒）。${stagingHint}`) ||
      (error.message && error.message.indexOf('timeout') !== -1 &&
        `請求逾時（約 ${timeoutSec} 秒）。${stagingHint}`) ||
      (error.message && error.message.indexOf('Network Error') !== -1 &&
        '網路錯誤（常為 CORS 或 Mixed Content：HTTPS 頁不得請求 http:// API）。Staging 請用 https://staging.xrross-soc.com；本機對 111 請 npm run dev:staging（/__staging111 代理）。') ||
      '請求失敗，請檢查網路或稍後再試'
    Message({
      message: msg,
      type: 'error',
      duration: 5 * 1000
    })
    return Promise.reject(error)
  }
)

export default service



// WEBPACK FOOTER //
// ./src/utils/request.js